import { useGetMediaFoldersQuery } from "@api/media-folders";
import { useEffect, useMemo, useState } from "react";
import type { IMediaFile } from "@/shared/types";
import { isFontMimeType } from "@/shared/utils/font-mime-type";
import { useMediaLibRevelantUploadingFiles } from "../../hooks";
import {
  type IMediaLibDialogItem,
  MediaLibCategoryEnum,
  MediaLibPresetEnum,
  type TElement,
} from "../../types";
import {
  MEDIA_LIB_PANEL_MASONRY_COLS,
  MEDIA_LIB_PANEL_MASONRY_GAPS,
} from "../main-panel/constants";
import { MediaLibFilesCtxProvider } from "./media-lib-files.ctx";
import { MediaLibFilesColumn } from "./media-lib-files-column";

interface IProps {
  files: IMediaFile[];

  category: MediaLibCategoryEnum;
  selectedFolderId: string | null;

  preset?: MediaLibPresetEnum;
  mediaPreset?: MediaLibPresetEnum;

  hasSearch?: boolean;

  updateFile: (id: string, data: Partial<IMediaFile>) => void;
  removeFile: (id: string) => void;

  onSelect?: (item: IMediaFile) => void;
  selectedItems?: IMediaLibDialogItem[];
  isMultiSelect?: boolean;
  view?: "panel" | "dialog";
  source?: "default" | "settings";
  elementWrapper?: React.ComponentType<{
    children: React.ReactNode;
    element: TElement;
  }>;
}

// Narrow viewports (chrome-extension side panel) can't fit
// MEDIA_LIB_PANEL_MASONRY_COLS columns of decent size. Drop to 2 when the
// viewport is below 640px.
const useResponsiveMasonryCols = (): number => {
  const getCols = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
      ? 2
      : MEDIA_LIB_PANEL_MASONRY_COLS;
  const [cols, setCols] = useState<number>(getCols);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 640px)");
    const handler = () => setCols(getCols());
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return cols;
};

export const MediaLibFiles = ({
  preset = MediaLibPresetEnum.All,
  files,
  category,
  selectedFolderId,
  hasSearch,
  ...props
}: IProps) => {
  const masonryCols = useResponsiveMasonryCols();
  const isArchive = category === MediaLibCategoryEnum.Archive;
  const { data: allFolders = [] } = useGetMediaFoldersQuery({
    archived: isArchive,
  });

  const uploadingFiles = useMediaLibRevelantUploadingFiles({
    selectedFolderId,
    category,
  });

  const columnsData = useMemo(() => {
    const filteredFiles = files.filter((file) => {
      // `url` is optional on the backend media model; a row without one can't
      // be previewed or inserted, and the video tile crashes on it.
      if (!file.url) return false;

      if (isArchive !== file.archived) {
        return false;
      }

      if (
        category === MediaLibCategoryEnum.Images ||
        preset === MediaLibPresetEnum.OnlyImages
      ) {
        return file.type.startsWith("image/") || isFontMimeType(file.type);
      }

      if (
        category === MediaLibCategoryEnum.Videos ||
        preset === MediaLibPresetEnum.OnlyVideos
      ) {
        return file.type.startsWith("video/");
      }

      return true;
    });

    const sortedFiles = filteredFiles.sort(
      (a, b) =>
        new Date(
          isArchive ? (b.updatedAt ?? b.createdAt) : b.createdAt,
        ).getTime() -
        new Date(
          isArchive ? (a.updatedAt ?? a.createdAt) : a.createdAt,
        ).getTime(),
    );

    const cols: IMediaFile[][] = Array.from(
      { length: masonryCols },
      () => [],
    );
    let index = uploadingFiles.length;

    for (const file of sortedFiles) {
      const shouldIncludeInArchive =
        category !== MediaLibCategoryEnum.Archive ||
        file.archived ||
        file.folderId;

      if (shouldIncludeInArchive) {
        const columnIndex = index % masonryCols;
        cols[columnIndex].push(file);
        index++;
      }
    }

    return cols;
  }, [
    preset,
    files,
    category,
    selectedFolderId,
    allFolders,
    uploadingFiles.length,
    hasSearch,
    isArchive,
    masonryCols,
  ]);

  return (
    <MediaLibFilesCtxProvider
      ctxState={{
        preset,
        category,
        selectedFolderId,
        ...props,
      }}
    >
      <div
        className="tw-grid tw-size-full"
        style={{
          gap: MEDIA_LIB_PANEL_MASONRY_GAPS,
          gridTemplateColumns: `repeat(${masonryCols}, 1fr)`,
        }}
      >
        {columnsData.map((col, colIdx) => (
          <MediaLibFilesColumn key={colIdx} colIdx={colIdx} files={col} />
        ))}
      </div>
    </MediaLibFilesCtxProvider>
  );
};
